import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosHeaders } from 'axios'
import React, { useContext, useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import CoachMultiSelect from '../../components/coach/CoachMultiSelect'
import { SERVER_URL, apiHeader } from '../../config'
import UserContext from '../../context/UserContext'
import { TrainingSessionGroup as TrainingSessionGroupType } from '../../types/training-session-group.type'

interface SharedWithCoach {
    coachId: number
    // Ajoutez d'autres propriétés si nécessaire
}

// Utilisation de Omit pour rendre l'id optionnel tout en gardant les autres propriétés de TrainingSessionGroupType
type ExtendedTrainingSessionGroup = Omit<TrainingSessionGroupType, 'id'> & {
    id?: number
    sharedWith?: SharedWithCoach[]
    // Ces propriétés sont déjà dans TrainingSessionGroupType, donc pas besoin de les redéfinir
}

interface CoachTrainingSessionGroupFormModalProps {
    show: boolean
    onHide: () => void
    group: ExtendedTrainingSessionGroup | null
}

const CoachTrainingSessionGroupFormModal: React.FC<CoachTrainingSessionGroupFormModalProps> = ({
    show,
    onHide,
    group,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Récupérer le contexte utilisateur
    const userContext = useContext(UserContext)
    const user = userContext as { tokenCoach: string; coachData: { id: number } } | null

    // Récupérer l'ID du coach connecté
    const currentCoachId = user?.coachData?.id

    const [selectedCoachIds, setSelectedCoachIds] = useState<number[]>([])

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: '',
            description: '',
        },
    })

    // Réinitialiser le formulaire quand le groupe change
    useEffect(() => {
        if (group) {
            reset({
                title: group.title,
                description: group.description ?? '',
            })
            // Charger les coachs partagés si on est en mode édition
            if (group.sharedWith) {
                setSelectedCoachIds(group.sharedWith.map((share) => share.coachId))
            } else {
                setSelectedCoachIds([])
            }
        } else {
            reset({
                title: '',
                description: '',
            })
            setSelectedCoachIds([])
        }
    }, [group, reset])

    const createMutation = useMutation({
        mutationFn: async (data: any) => {
            const tokenCoach = localStorage.getItem('tokenCoach')!
            const headers = new AxiosHeaders()
            headers.set('Authorization', `Bearer ${tokenCoach}`)

            const response = await axios.post(
                `${SERVER_URL}/training-session-groups`,
                {
                    ...data,
                    coachId: 1, // Remplacer par l'ID du coach connecté
                    sharedWithCoachIds: selectedCoachIds,
                },
                apiHeader(tokenCoach)
            )
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trainingSessionGroups'] })
            toast.success(t('coach.trainingSessionGroups.createSuccess', 'Le groupe a été créé avec succès'))
            onHide()
        },
        onError: () => {
            toast.error(
                t('coach.trainingSessionGroups.createError', 'Une erreur est survenue lors de la création du groupe')
            )
        },
    })

    const updateMutation = useMutation({
        mutationFn: async (data: any) => {
            const tokenCoach = localStorage.getItem('tokenCoach')!
            const headers = new AxiosHeaders()
            headers.set('Authorization', `Bearer ${tokenCoach}`)

            const response = await axios.put(
                `${SERVER_URL}/training-session-groups/${group?.id}`,
                {
                    ...data,
                    sharedWithCoachIds: selectedCoachIds,
                },
                apiHeader(tokenCoach)
            )
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trainingSessionGroups'] })
            toast.success(t('coach.trainingSessionGroups.updateSuccess', 'Le groupe a été mis à jour avec succès'))
            onHide()
        },
        onError: () => {
            toast.error(
                t('coach.trainingSessionGroups.updateError', 'Une erreur est survenue lors de la mise à jour du groupe')
            )
        },
    })

    const onSubmit = async (data: any) => {
        setIsSubmitting(true)
        try {
            if (group) {
                await updateMutation.mutateAsync(data)
            } else {
                await createMutation.mutateAsync(data)
            }
        } catch (error) {
            console.error('Error saving training session group:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const getButtonText = () => {
        if (group) {
            return t('common.saveChanges', 'Enregistrer les modifications')
        }
        return t('common.create', 'Créer')
    }

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {group
                        ? t('coach.trainingSessionGroups.editGroup', 'Modifier le groupe')
                        : t('coach.trainingSessionGroups.newGroup', 'Nouveau groupe')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body>
                    <Form.Group className="mb-3" controlId="title">
                        <Form.Label>{t('common.title', 'Titre')}</Form.Label>
                        <Form.Control
                            type="text"
                            {...register('title', {
                                required: t('validation.required', 'Ce champ est requis'),
                            })}
                            isInvalid={!!errors.title}
                        />
                        <Form.Control.Feedback type="invalid">{errors.title?.message as string}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="description">
                        <Form.Label>{t('common.description', 'Description')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            {...register('description')}
                            isInvalid={!!errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.description?.message as string}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <CoachMultiSelect
                        selectedCoachIds={selectedCoachIds}
                        onChange={setSelectedCoachIds}
                        excludeCoachId={currentCoachId}
                        className="mt-4"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <output className="d-inline-flex align-items-center me-1">
                                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                    <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
                                </output>
                                {t('common.saving', 'Enregistrement...')}
                            </>
                        ) : (
                            getButtonText()
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default CoachTrainingSessionGroupFormModal
